import { RESUME_ACTION } from "./resumeContracts";
/**
 * Whether the boot may skip re-fetching the aux metadata discovery already
 * sent — lesson name, assessment state, completed bites, bite metrics, notepad
 * flags, cached bite. Roughly 208ms p50 of `ensure_session`.
 *
 * A deferral needs this and the server's own gate
 * (`_ensure_session_aux_deferral_gate_reason`) to agree: only the client knows
 * what the student did in between, and only the server can refuse to be talked
 * into serving stale data. Reason strings are named alike on both sides so the
 * two halves line up in the metrics.
 */

import { TutorMode } from "@/constants/mode";

export type AuxDeferralReason =
  | "eligible"
  | "aux_not_loaded"
  | "cached_bite"
  | "preview"
  | "lti"
  | "non_public_mode"
  | "start_choice";

export interface AuxDeferralInput {
  /** Discovery ran and populated the aux-backed stores. */
  startupAuxMetadataReady: boolean;
  /** The `apply_resume_action` the student triggered since discovery, if any. */
  startChoice: string | null;
  /** The boot is answering a mid-lesson cached-bite prompt. */
  cachedBitePromptActive: boolean;
  /** The `?slideIndex` URL parameter, unparsed. */
  previewSlideIndex: string | null;
  ltiLaunchRequestId: string | null | undefined;
  mode: string;
}

/**
 * Resume leaves the aux block's inputs untouched; both restarts erase them and
 * get a freshly read replacement in the `apply_resume_action` response.
 *
 * Listing an action here does not claim that replacement arrived -- when it
 * does not, the container clears `startupAuxMetadataReady`, and the check above
 * this one refuses the deferral on that ground instead.
 */
// Set<string>, deliberately: the members are constant-sourced, but what is
// looked up arrives off the wire as a plain string.
const AUX_DEFERRABLE_START_CHOICES = new Set<string>([
  RESUME_ACTION.RESUME_BITE,
  RESUME_ACTION.RESTART_BITE,
  RESUME_ACTION.RESTART_LESSON,
]);

export const resolveAuxMetadataDeferral = ({
  startupAuxMetadataReady,
  startChoice,
  cachedBitePromptActive,
  previewSlideIndex,
  ltiLaunchRequestId,
  mode,
}: AuxDeferralInput): { canDefer: boolean; reason: AuxDeferralReason } => {
  // Order mirrors the server gate so a disagreement is readable side by side.
  if (!startupAuxMetadataReady) {
    return { canDefer: false, reason: "aux_not_loaded" };
  }
  if (cachedBitePromptActive) {
    return { canDefer: false, reason: "cached_bite" };
  }
  if (previewSlideIndex !== null) {
    return { canDefer: false, reason: "preview" };
  }
  if (ltiLaunchRequestId) {
    return { canDefer: false, reason: "lti" };
  }
  if (mode !== TutorMode.PUBLIC) {
    return { canDefer: false, reason: "non_public_mode" };
  }
  // Fail closed: an action added later must not inherit resume's permission.
  if (startChoice !== null && !AUX_DEFERRABLE_START_CHOICES.has(startChoice)) {
    return { canDefer: false, reason: "start_choice" };
  }
  return { canDefer: true, reason: "eligible" };
};
